import { useLocalStorage } from "@circle-react/hooks/utils/useLocalStorage";
import { useLocalStorageState } from "@circle-react/hooks/utils/useLocalStorageState";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { t } from "@/i18n-js/instance";
import { useElectronNotification } from "@/react/hooks/notifications/useElectronNotification";

export const TABS = {
  INBOX: "inbox",
  CONTENT: "content",
  MENTIONS: "mentions",
  FOLLOWING: "following",
  MODERATION: "moderation",
  ALL: "all",
  ARCHIVED: "archived",
} as const;

export type Tab = (typeof TABS)[keyof typeof TABS];

const TAB_TRANSLATION_KEYS: Record<Tab, string> = {
  [TABS.INBOX]: "notifications.group_tabs.inbox",
  [TABS.CONTENT]: "notifications.group_tabs.content",
  [TABS.MENTIONS]: "notifications.group_tabs.mentions",
  [TABS.FOLLOWING]: "notifications.group_tabs.following",
  [TABS.MODERATION]: "notifications.group_tabs.moderation",
  [TABS.ALL]: "notifications.group_tabs.all",
  [TABS.ARCHIVED]: "notifications.group_tabs.archived",
} as const;

export const FILTERS = {
  ALL: "all",
  UNREAD: "unread",
  ARCHIVED: "archived",
} as const;

export type Filter = (typeof FILTERS)[keyof typeof FILTERS];

export interface NotificationContextProps {
  activeFilter: Filter;
  activeTab: Tab;
  getTabLabel: (tab: Tab) => string;
  isBannerDismissed: boolean;
  lastMarkedAsRead: number | null;
  notificationCount: number;
  onClose?: () => void;
  setActiveFilter: (filter: Filter) => void;
  setActiveTab: (tab: Tab) => void;
  setBannerDismissed: Dispatch<SetStateAction<boolean | undefined>>;
  setLastMarkedAsRead: (lastMarkedAsRead: number | null) => void;
  setNotificationCount: (count: number) => void;
  setNotificationTabCounters: (notifications: {
    inbox: number;
    mentions: number;
    notifications: number;
  }) => void;
}

const NotificationContext = createContext<NotificationContextProps | null>(
  null,
);

NotificationContext.displayName = "NotificationContext";

const NOTIFICATION_LAST_MARKED_AS_READ_KEY = "notification_last_marked_as_read";
const BANNER_DISMISSED_KEY = "notifications_banner_dismissed_v1";

export interface NotificationProviderProps {
  children: ReactNode;
  onClose?: () => void;
}

export const NotificationProvider = ({
  children,
  onClose,
}: NotificationProviderProps) => {
  const getTabLabel = useCallback(
    (tab: Tab) => t(TAB_TRANSLATION_KEYS[tab]),
    [],
  );
  const { setDesktopAppNotificationCount } = useElectronNotification();

  const [activeTab, setActiveTab] = useState<Tab>(TABS.INBOX);
  const [activeFilter, setActiveFilter] = useState<Filter>("all");

  const [notificationTabCounters, setNotificationTabCounters] = useState({
    inbox: 0,
    mentions: 0,
    notifications: 0,
  });
  const { setValue: setLastMarkedAsRead, getValue } = useLocalStorage<
    number | null
  >(NOTIFICATION_LAST_MARKED_AS_READ_KEY);

  const lastMarkedAsRead = getValue() || null;

  const [isBannerDismissedRaw, setBannerDismissed] =
    useLocalStorageState<boolean>(BANNER_DISMISSED_KEY, false);
  const isBannerDismissed = Boolean(isBannerDismissedRaw);
  const { notifications } = notificationTabCounters;
  const notificationCount = notifications;

  const setNotificationCount = (count: number) => {
    setNotificationTabCounters({
      ...notificationTabCounters,
      notifications: count,
    });
  };

  useEffect(() => {
    setDesktopAppNotificationCount(notificationCount);
  }, [notificationCount, setDesktopAppNotificationCount]);

  return (
    <NotificationContext.Provider
      value={{
        activeTab,
        setActiveTab,
        notificationCount,
        setNotificationCount,
        getTabLabel,
        isBannerDismissed,
        onClose,
        setBannerDismissed,
        setNotificationTabCounters,
        lastMarkedAsRead,
        setLastMarkedAsRead,
        setActiveFilter,
        activeFilter,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider",
    );
  }

  return context;
};
